import * as React from 'react'
import { ContextFrom, InterpreterFrom } from 'xstate'

import { TFetchUserTokenParams } from '~config/serverEndpoints'

import { authMachine } from '../service/authMachine'

type TContext = ContextFrom<typeof authMachine>
type TDefinedAuthContext = {
  userToken: Defined<TContext['userToken']>
  userId: Defined<TContext['userId']>
}

type TUpdaters = {
  login: (payload: TFetchUserTokenParams) => void
  setAuth: (payload: TDefinedAuthContext) => void
  logout: () => void
  clearError: () => void
}

type TOptions = {
  machine: InterpreterFrom<typeof authMachine>
}

/**
 * Returns functions to change the auth state (`login`, `logout`)
 *
 * ! the `logout` callback does NOT call `logoutUser` mutation
 */
export const useAuthUpdaters = ({ machine }: TOptions) => {
  const setAuth: TUpdaters['setAuth'] = React.useCallback(
    (payload) => machine.send({ type: 'SET_AUTH_DATA', payload }),
    [machine],
  )

  const login: TUpdaters['login'] = React.useCallback(
    (payload) => machine.send({ type: 'LOGIN', payload }),
    [machine],
  )

  const logout: TUpdaters['logout'] = React.useCallback(() => {
    machine.send({ type: 'LOGOUT' })
  }, [machine])

  const clearError: TUpdaters['clearError'] = React.useCallback(() => {
    machine.send({ type: 'CLEAR_ERROR' })
  }, [machine])

  const updaters = React.useMemo(() => {
    return { login, logout, setAuth, clearError }
  }, [clearError, login, logout, setAuth])

  return updaters
}
